import { cn } from '../../../lib/utils.ts'
import { Heading } from '../heading/heading.tsx'
import { Sidebar } from '../sidebar/sidebar.tsx'

/**
 * `View` is the screen frame: `View.Root` + `View.Header` (with `View.Title`) + `View.Actions`
 * + `View.Body` + `View.Footer`. No data, no domain -- its one app-level tie is `View.Header`, which
 * carries the navigation trigger when a `Sidebar.Provider` is mounted above it, and nothing otherwise.
 * Compose a screen by assembling these slots; use `View.Title` for the page heading so every screen
 * gets the same responsive title sizing for free (drop a raw `Heading` only for a deliberate
 * one-off). The matching pending skeleton (`patterns/view-skeleton`) reuses the same frame so the
 * crossfade has no layout shift.
 *
 * From desktop up, `View.Root` caps the reading column at 800px of content and centers it in the
 * routed region, so every screen shares one content width instead of stretching to the viewport. The
 * cap includes the page gutter the slots add, so a wide window gives 800px of content (the gutter is
 * absorbed by the centering). Below desktop, it fills the routed region so the sticky header spans it
 * while each slot keeps its own page gutter.
 *
 * @summary Composable view frame (header / title / actions / body / footer).
 */
function ViewRoot(props: ViewRoot.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="view-root"
      className={cn(
        `
          group/view mx-auto flex min-h-full w-full flex-col
          lg:max-w-[calc(800px_+_2*var(--spacing-page))]
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ViewRoot {
  export type Props = React.ComponentProps<'div'>
}

function ViewHeader(props: ViewHeader.Props) {
  const { className, children, ...rest } = props
  return (
    <div
      data-slot="view-header"
      // No bottom padding: the gap down to the body is the body's own top gutter.
      // Sticky on mobile only: the bar there has a background and an elevation, which is what lets
      // content pass under it. On desktop the header is transparent, so it scrolls with the page.
      className={cn(
        'flex items-center gap-stack px-page pt-page',
        `max-lg:sticky max-lg:top-0 max-lg:z-10 max-lg:bg-surface-base-normal max-lg:py-stack`,
        className,
      )}
      {...rest}
    >
      {/* The mobile nav opens from the page header row, immediately left of the title (Figma). Renders
          nothing when no `Sidebar.Provider` is mounted, so a `View` outside the shell is unaffected. */}
      <Sidebar.Trigger className="lg:hidden" />
      {children}
    </div>
  )
}
namespace ViewHeader {
  export type Props = React.ComponentProps<'div'>
}

// The canonical view title: the `h4` visual size on the `h1` tag, per the Figma view frames — the
// document outline wants h1, the page hierarchy wants h4. The FO typescale already downsizes it
// (28/32 desktop, 22/28 mobile), so no responsive class is needed here.
function ViewTitle(props: ViewTitle.Props) {
  // `className` flows straight through: Heading merges it into its own variant classes.
  return <Heading variant="h4" level={1} data-slot="view-title" className="truncate" {...props} />
}
namespace ViewTitle {
  export type Props = React.ComponentProps<'h1'>
}

function ViewActions(props: ViewActions.Props) {
  const { className, children, ...rest } = props
  if (children == null || typeof children === 'boolean') return null

  return (
    <div
      data-slot="view-actions"
      className={cn(
        `
          ml-auto flex shrink-0 items-center gap-inline
          empty:hidden
        `,
        `
          max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-20 max-lg:flex-col max-lg:items-stretch max-lg:border-t
          max-lg:border-line-base-basic max-lg:bg-background max-lg:p-50
          max-lg:pb-[calc(var(--spacing-50)+env(safe-area-inset-bottom))]
        `,
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  )
}
namespace ViewActions {
  export type Props = React.ComponentProps<'div'>
}

// Renders the `<main>` landmark: one View == one screen, so the body is the view's primary content
// region. Keeps content inside a landmark and gives skip links a target. `flex-1` makes it take the
// slack on a short page, which keeps the footer at the bottom; the scrolling itself belongs to
// `Layout.Body`.
function ViewBody(props: ViewBody.Props) {
  const { className, ...rest } = props
  return (
    <main
      data-slot="view-body"
      // Mobile padding clears the rendered fixed actions bar so the last content can scroll into view.
      className={cn(
        // TODO: Add the design-system token `mobile-action-bar-clearance` and replace 105px.
        `
          flex flex-1 flex-col gap-section p-page
          max-lg:group-has-[[data-slot=view-actions]:not(:empty)]/view:pb-[105px]
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace ViewBody {
  export type Props = React.ComponentProps<'main'>
}

function ViewFooter(props: ViewFooter.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="view-footer"
      className={cn(
        'flex items-center justify-end gap-inline border-t border-line-base-basic px-page py-stack',
        className,
      )}
      {...rest}
    />
  )
}
namespace ViewFooter {
  export type Props = React.ComponentProps<'div'>
}

const View = {
  Root: ViewRoot,
  Header: ViewHeader,
  Title: ViewTitle,
  Actions: ViewActions,
  Body: ViewBody,
  Footer: ViewFooter,
}

namespace View {
  export namespace Root {
    export type Props = ViewRoot.Props
  }
  export namespace Header {
    export type Props = ViewHeader.Props
  }
  export namespace Title {
    export type Props = ViewTitle.Props
  }
  export namespace Actions {
    export type Props = ViewActions.Props
  }
  export namespace Body {
    export type Props = ViewBody.Props
  }
  export namespace Footer {
    export type Props = ViewFooter.Props
  }
}

export { View }
